import React from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';

import { Box, Text, Flex, xcss, Pressable } from '@atlaskit/primitives';
import QuestionCircleIcon from '@atlaskit/icon/core/question-circle';
import CloseIcon from '@atlaskit/icon/core/close';
import { IconButton } from '@atlaskit/button/new';

import {
	useSetOnboardingState,
	useGetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { ONBOARDING_EXPERIENCES } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import { ErrorDisplay } from '@confluence/error-boundary';

const { IS_CONTENT_WRAPPER_LABELS_TOUR_DISMISSED } = ONBOARDING_EXPERIENCES;

const boxStyle = xcss({
	width: '100%',
	maxWidth: '900px',
	borderStyle: 'solid',
	borderColor: 'color.border',
	borderWidth: 'border.width',
	margin: '0 auto',
});

const linkButtonStyle = xcss({
	color: 'color.link',
	marginLeft: 'space.050',
});

type LabelsMovedChangeboardingProps = {
	onShowMeClick?: () => void;
};

export const LabelsMovedChangeboarding = ({ onShowMeClick }: LabelsMovedChangeboardingProps) => {
	const { setOnboardingState } = useSetOnboardingState();

	const { data, error, loading } = useGetOnboardingState([
		IS_CONTENT_WRAPPER_LABELS_TOUR_DISMISSED,
	]);

	const isDismissed = data?.onboardingState[0].value === 'true';

	if (isDismissed || loading) {
		// This is just a spacer to keep some distance between the content and footer.
		// When this changeboarding is eventually removed, we should probably just make this be a bottom margin on content or a top margin on the footer.
		return <Box padding="space.200" />;
	}

	if (error) {
		return <ErrorDisplay error={error} />;
	}

	return (
		<Box xcss={boxStyle} padding="space.200" data-vc="labels-moved-changeboarding">
			<Flex justifyContent="space-between" alignItems="center">
				<Flex gap="space.100" alignItems="center" justifyContent="start">
					<QuestionCircleIcon label="" />
					<Text color="color.text.subtle">
						<FormattedMessage
							{...i18n.labelsMoved}
							values={{
								bold: (chunks: React.ReactNode[]) => (
									<Text color="color.text" weight="medium">
										{chunks}
									</Text>
								),
								link: (chunks: React.ReactNode[]) => (
									<Pressable
										onClick={onShowMeClick}
										padding="space.0"
										backgroundColor="color.background.neutral.subtle"
										xcss={linkButtonStyle}
									>
										{chunks}
									</Pressable>
								),
							}}
						/>
					</Text>
				</Flex>
				<IconButton
					icon={CloseIcon}
					label={<FormattedMessage {...i18n.dismiss} />}
					appearance="subtle"
					spacing="compact"
					onClick={() => {
						setOnboardingState({ key: IS_CONTENT_WRAPPER_LABELS_TOUR_DISMISSED, value: 'true' });
					}}
				/>
			</Flex>
		</Box>
	);
};

const i18n = defineMessages({
	labelsMoved: {
		id: 'labels.labels-moved-changeboarding.message',
		defaultMessage:
			'<bold>Looking for labels?</bold> They can now be found in the details panel on the floating action bar. <link>Show me</link>',
		description:
			'The message shown when the labels have been moved to the details panel. This appears at the bottom of the page where labels were previously seen. The first part of the message is intended to be bolded. The last part of the message is intended to be a link which activates a spotlight showing the new location of labels.',
	},
	dismiss: {
		id: 'labels.labels-moved-changeboarding.dismiss',
		defaultMessage: 'Dismiss',
		description:
			'The accessible label text for the dismiss button on the labels moved changeboarding. Appears as an X close icon button to the right of labels.changeboarding.message.',
	},
});
